/**
 * Connection Status Component
 *
 * Shows the current SSE connection status with visual indicator.
 * Provides option to manually reconnect.
 */

import { useState, useEffect, useCallback } from 'react';
import { sseClient } from '../lib/sseClient';
import { useAuth } from '../features/auth';

interface ConnectionStatusProps {
  showAlways?: boolean;
  position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
}

export function ConnectionStatus({
  showAlways = false,
  position = 'bottom-left',
}: ConnectionStatusProps) {
  const { isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [failedChecks, setFailedChecks] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // Check connection status
  const checkConnection = useCallback(() => {
    const connected = sseClient.isConnected();
    setIsConnected(connected);
    // Only increment failed checks after initial load delay
    if (!connected) {
      setFailedChecks((prev) => prev + 1);
    } else {
      setFailedChecks(0);
      setDismissed(false);
    }
  }, []);

  // Manual reconnect
  const handleReconnect = useCallback(() => {
    setIsReconnecting(true);
    sseClient.disconnect();
    sseClient.connect();

    // Check status after a short delay
    setTimeout(() => {
      checkConnection();
      setIsReconnecting(false);
    }, 2000);
  }, [checkConnection]);

  // Poll connection status
  useEffect(() => {
    checkConnection();
    const interval = setInterval(checkConnection, 5000);
    return () => clearInterval(interval);
  }, [checkConnection]);

  // Position classes
  const positionClasses = {
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
  };

  // Don't show on login page / when not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Don't show if user dismissed
  if (dismissed) {
    return null;
  }

  // Only show when disconnected (unless showAlways is true)
  if (!showAlways && isConnected) {
    return null;
  }

  // Don't show until we've had at least 3 failed checks (15+ seconds)
  // This prevents flashing on initial load or brief disconnects
  if (failedChecks < 3) {
    return null;
  }

  return (
    <div
      data-testid="connection-status"
      data-status={isConnected ? 'connected' : 'disconnected'}
      className={`fixed ${positionClasses[position]} z-40`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg text-sm ${
          isConnected
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
        }`}
      >
        {/* Status indicator */}
        <span
          className={`w-2 h-2 rounded-full ${
            isConnected ? 'bg-green-500' : 'bg-red-500 animate-pulse'
          }`}
        />

        {/* Status text */}
        <span>
          {isReconnecting
            ? 'Verbinding herstellen...'
            : isConnected
            ? 'Verbonden'
            : 'Geen verbinding'}
        </span>

        {/* Reconnect button (only when disconnected) */}
        {!isConnected && !isReconnecting && (
          <button
            onClick={handleReconnect}
            className="ml-2 text-red-600 hover:text-red-800 underline"
          >
            Opnieuw verbinden
          </button>
        )}

        {/* Loading spinner when reconnecting */}
        {isReconnecting && (
          <svg
            className="w-4 h-4 animate-spin text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}

        {/* Dismiss button */}
        {!isConnected && !isReconnecting && (
          <button
            onClick={() => setDismissed(true)}
            className="ml-1 text-red-400 hover:text-red-600"
            title="Melding verbergen"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
